import type { MouseEvent } from 'react';
import { useCallback } from 'react';

import type { ListItemType } from 'components/ui/lists/CommonList';

import { ListSectionItemTitle } from 'components/ui/lists/CommonList';
import { useMenuContext } from 'components/ui/menus/menuContext';

import { DefaultItemIcon, InMenuItem, StandaloneMenuItem } from './MenuButtonItem.styles';

type Props = ListItemType & {
  /**
   * Whether the item is inert. Inside a menu this also takes it out of the arrow-key order.
   */
  disabled?: boolean;
};

const DEFAULT_ICON = <DefaultItemIcon />;

/**
 * A row inside a `MenuButton` dropdown, exported to consumers as `MenuItem`.
 *
 * Renders as a Radix `Item` when there is a menu around it — carrying `role="menuitem"`, arrow-key
 * roving and typeahead — and falls back to `ListItem` when there is not, so rendering one on its own
 * still works. Radix owns the element in the menu case, which is what lets an item hold a
 * `listItemElementOverride` that forwards no ref.
 */
const MenuButtonItem = ({
  className,
  disabled = false,
  icon = DEFAULT_ICON,
  iconPosition = 'trailing',
  label,
  labelElement,
  listItemElementOverride,
  onClick,
  onMouseEnter,
  testId,
  titleCss,
}: Props) => {
  const menu = useMenuContext();

  /*
   * Radix closes the menu after a select unless the event is prevented, which is how `autoClose`
   * maps onto it.
   */
  const handleSelect = useCallback(
    (event: Event) => {
      if (!menu?.autoClose) {
        event.preventDefault();
      }
    },
    [menu?.autoClose]
  );

  /*
   * The item's own `onClick` rides on the element rather than on `onSelect`, so it receives a real
   * mouse event and fires for keyboard activation too -- Radix turns Enter and Space into a DOM
   * click. `ListItemType` types the handler against a button because that is what the standalone
   * variant renders; in a menu the element is Radix's div, and the two event shapes differ only in
   * `currentTarget`, which no caller reads.
   *
   * A press that started inside an override is left to the override's own control: the override
   * replaced the whole row before this element carried a handler, and running both fires two actions
   * for one press. Radix dispatches keyboard activation on the row itself though, so a press that
   * lands directly on the row -- Enter, Space, or a click on the row's own padding -- is forwarded to
   * the override's control instead of running the item's `onClick` beside it, since that control is
   * often wired to a different action than the row (e.g. an external link with no `onClick` at all).
   *
   * Radix's `disabled` guards `onSelect`, not a handler on the element, and the row's own
   * `pointer-events: none` is styling rather than behaviour -- so the disabled case is checked here
   * too, where the prop's promise of an inert row actually lives.
   */
  const handleClick = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      if (disabled) {
        return;
      }

      if (listItemElementOverride) {
        if (event.target === event.currentTarget) {
          event.currentTarget.querySelector<HTMLElement>('a, button')?.click();
        }

        return;
      }

      onClick?.(event as unknown as MouseEvent<HTMLButtonElement>);
    },
    [disabled, listItemElementOverride, onClick]
  );

  if (!menu?.isInRadixMenu) {
    return (
      <StandaloneMenuItem
        className={className}
        disabled={disabled}
        icon={icon}
        iconPosition={iconPosition}
        label={label}
        labelElement={labelElement}
        listItemElementOverride={listItemElementOverride}
        onClick={onClick}
        onMouseEnter={onMouseEnter}
        testId={testId}
        titleCss={titleCss}
      />
    );
  }

  return (
    <InMenuItem
      $hasOverride={listItemElementOverride != null}
      className={className}
      data-testid={testId}
      disabled={disabled}
      onClick={handleClick}
      onMouseEnter={onMouseEnter}
      onSelect={handleSelect}
      textValue={label}
    >
      {listItemElementOverride ?? (
        <ListSectionItemTitle $isLeadingIcon={icon ? iconPosition === 'leading' : undefined} css={titleCss}>
          {icon && iconPosition === 'leading' ? icon : null}
          {labelElement || label}
          {icon && iconPosition === 'trailing' ? icon : null}
        </ListSectionItemTitle>
      )}
    </InMenuItem>
  );
};

export default MenuButtonItem;
